import React, { useState, useEffect, useCallback } from 'react';
import { ContestPhaseTab, IContestPhaseTab, PhaseData } from './ContestPhaseTab';
import { Box, Button, FormLabel, IconButton, Stack, Typography, Alert } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import dayjs from 'dayjs';
import { ColorScheme, theme } from '../../constants/theme';
import AddIcon from '@mui/icons-material/Add';
import { GenericModal } from '../GenericModal/GenericModal';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker';
import { StyleSheet } from 'react-native';
import { MainTagCarousel } from '../EsCarousels/MainTagCarousel';
import { showToastMessage } from '../../utils/AppUtils';

type Phase = {
    phase: string;
    date_started: string;
    expiration_date: string;
};

interface IContestPhaseSelector extends Omit<IContestPhaseTab, 'data' | 'index'> {
    phases: PhaseData[];
    minStartDay?: string;
    minEndDay?: string;
    onChange?: (arg: Phase) => void;
    onUpdatePhase?: (arg: Phase, index: number) => void;
    onValidationError?: (error: string) => void;
    disableVotingPhase?: boolean;
    readOnly?: boolean;
}

let CONTEST_PHASE = ['submission', 'ideation', 'pitch', 'vote', 'production', 'judging', 'event'];

const ContestPhaseSelector: React.FC<IContestPhaseSelector> = ({
    phases,
    onRemovePhase,
    onChange,
    onUpdatePhase,
    onValidationError,
    minStartDay,
    minEndDay,
    disableVotingPhase,
    readOnly,
}) => {
    const { themeStyle, device } = useThemeContext();
    const [open, setOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [validationError, setValidationError] = useState<string>('');
    const [selectedPhase, setSelectedPhase] = useState<string>('');
    const styles = dynamicStyles(themeStyle);
    const [value, setValue] = useState({
        phase: '',
        date_started: '',
        expiration_date: '',
    });

    if (disableVotingPhase) {
        CONTEST_PHASE = CONTEST_PHASE.filter(phase => phase !== 'vote');
    }

    // Validate that all phases are in chronological order
    const validateAllPhases = useCallback((): string => {
        if (phases.length <= 1) return ''; // No validation needed for 0 or 1 phase

        const sortedPhases = sortPhasesChronologically(phases);

        for (let i = 0; i < sortedPhases.length - 1; i++) {
            const currentPhase = sortedPhases[i];
            const nextPhase = sortedPhases[i + 1];

            if (dayjs(currentPhase.expiration_date).isAfter(dayjs(nextPhase.date_started))) {
                return `Phase "${currentPhase.phase}" ends after "${nextPhase.phase}" starts`;
            }
        }

        return '';
    }, [phases]);

    // Validate all phases whenever phases change
    useEffect(() => {
        if (phases.length > 1) {
            const error = validateAllPhases();
            if (error && onValidationError) {
                onValidationError(error);
            }
        }
    }, [phases, onValidationError, validateAllPhases]);

    const handleOnChange = (field: string, value: string) => {
        setValue(prevValue => ({
            ...prevValue,
            [field]: value,
        }));
        setValidationError(''); // Clear validation error when user makes changes

        // Update selected phase when phase field changes
        if (field === 'phase') {
            setSelectedPhase(value);
        }
    };

    const validatePhase = (newPhase: Phase, excludeIndex?: number | null): string => {
        const startDate = dayjs(newPhase.date_started);
        const endDate = dayjs(newPhase.expiration_date);

        // Check if start date is before end date
        if (startDate.isAfter(endDate)) {
            return 'Start date must be before end date';
        }

        // Check for overlaps with existing phases
        for (let i = 0; i < phases.length; i++) {
            if (excludeIndex !== undefined && excludeIndex !== null && i === excludeIndex) continue;

            const existingPhase = phases[i];
            const existingStart = dayjs(existingPhase.date_started);
            const existingEnd = dayjs(existingPhase.expiration_date);

            // Check if new phase overlaps with existing phase
            if (
                (startDate.isBefore(existingEnd) && endDate.isAfter(existingStart)) ||
                startDate.isSame(existingStart) ||
                endDate.isSame(existingEnd) ||
                startDate.isSame(existingEnd) ||
                endDate.isSame(existingStart)
            ) {
                return `Phase "${existingPhase.phase}" overlaps with this time period`;
            }
        }

        // Check if phases are in chronological order
        const sortedPhases = [...phases]
            .filter((_, i) => excludeIndex !== undefined && excludeIndex !== null && i !== excludeIndex)
            .sort((a, b) => dayjs(a.date_started).valueOf() - dayjs(b.date_started).valueOf());

        if (sortedPhases.length > 0) {
            // Find the phase that should come before this one
            let previousPhase: PhaseData | null = null;
            for (const phase of sortedPhases) {
                if (dayjs(phase.date_started).isAfter(startDate)) {
                    break;
                }
                previousPhase = phase;
            }

            if (previousPhase && dayjs(previousPhase.expiration_date).isAfter(startDate)) {
                return `This phase should start after "${previousPhase.phase}" ends`;
            }
        }

        return '';
    };

    // Helper function to sort phases chronologically
    const sortPhasesChronologically = (phasesToSort: PhaseData[]): PhaseData[] => {
        return [...phasesToSort].sort((a, b) => dayjs(a.date_started).valueOf() - dayjs(b.date_started).valueOf());
    };

    const handleEditPhase = (phaseData: PhaseData, index: number) => {
        setValue({
            phase: phaseData.phase,
            date_started: phaseData.date_started,
            expiration_date: phaseData.expiration_date,
        });
        setSelectedPhase(phaseData.phase); // Set the selected phase for the carousel
        setEditingIndex(index);
        setOpen(true);
    };

    const onSubmit = () => {
        const error = validatePhase(value, editingIndex);
        if (error) {
            setValidationError(error);
            showToastMessage(error, 'error');
            return;
        }

        if (editingIndex !== null) {
            // Update existing phase
            onUpdatePhase && onUpdatePhase(value, editingIndex);
        } else {
            // Add new phase
            onChange && onChange(value);
        }

        // Reset form
        setValue({
            phase: '',
            date_started: '',
            expiration_date: '',
        });
        setEditingIndex(null);
        setSelectedPhase('');
        setValidationError('');
        setOpen(false);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingIndex(null);
        setValue({
            phase: '',
            date_started: '',
            expiration_date: '',
        });
        setSelectedPhase('');
        setValidationError('');
    };

    // Get overall validation status for display
    const overallValidationError = validateAllPhases();

    return (
        <Stack direction="row" flexWrap="wrap" alignItems="center">
            {/* Show overall validation error if any */}
            {overallValidationError && (
                <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                    {overallValidationError}
                </Alert>
            )}

            {phases.map((phase, index) => (
                <ContestPhaseTab
                    data={phase}
                    key={index}
                    onRemovePhase={phase => onRemovePhase && onRemovePhase(phase, index)}
                    onEditPhase={handleEditPhase}
                    index={index}
                    readOnly={readOnly}
                />
            ))}
            <GenericModal
                open={open}
                handleClose={handleClose}
                paperStyle={{
                    width: '100%',
                }}>
                <Box sx={{ position: 'relative', height: '100%' }}>
                    <Box style={styles.flexColumn} sx={{ gap: '8px', width: '100%' }}>
                        <Typography
                            sx={{
                                fontSize: 18,
                                fontWeight: '600',
                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                color: theme[themeStyle].highlight,
                                marginBottom: 2,
                            }}>
                            {editingIndex !== null ? 'Edit Phase' : 'Add New Phase'}
                        </Typography>

                        {validationError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {validationError}
                            </Alert>
                        )}

                        <MainTagCarousel
                            carouselTitle="Select Phase Title"
                            titleStyle={{
                                textAlign: 'left',
                                fontSize: 14,
                                fontStyle: 'normal',
                                fontWeight: '400',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                lineHeight: 2.5,
                                color: theme[themeStyle].form_label,
                                marginBottom: '10px',
                            }}
                            list={CONTEST_PHASE.filter(tag =>
                                editingIndex !== null
                                    ? phases[editingIndex].phase === tag || phases.every(e => e.phase !== tag)
                                    : phases.every(e => e.phase !== tag),
                            )}
                            disableViewAll
                            onSelectTag={tag => handleOnChange('phase', tag)}
                            initialValue={selectedPhase}
                            disregardParams
                            style={{
                                marginBottom: 20,
                                display: 'block',
                            }}
                            listStyle={{
                                flexWrap: 'wrap',
                                gap: '10px',
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '20px',
                            width: '100%',
                        }}>
                        <Box style={styles.flexColumn} sx={{ width: '100%', height: 'fit-content', gap: '8px' }}>
                            <FormLabel style={styles.text}>Start date</FormLabel>
                            <CustomDatePicker
                                minDate={minStartDay ? dayjs(minStartDay) : dayjs()}
                                onChange={(e, timezone) => {
                                    handleOnChange('date_started', e);
                                    if (timezone) {
                                        handleOnChange('timezone', timezone);
                                    }
                                }}
                                withTime
                                value={value.date_started}
                                sx={{ backgroundColor: theme[themeStyle].form_gray }}
                            />
                        </Box>
                        <Box style={styles.flexColumn} sx={{ width: '100%', height: 'fit-content', gap: '8px' }}>
                            <FormLabel style={styles.text}>End date</FormLabel>
                            <CustomDatePicker
                                onChange={(e, timezone) => {
                                    handleOnChange('expiration_date', e);
                                    if (timezone) handleOnChange('timezone', timezone);
                                }}
                                minDate={minEndDay ?? dayjs(value.date_started)}
                                withTime
                                value={value.expiration_date}
                                sx={{ backgroundColor: theme[themeStyle].form_gray }}
                            />
                        </Box>
                    </Box>
                    <Button
                        onClick={onSubmit}
                        style={styles.detailsButton}
                        disabled={!value.phase || !value.expiration_date || !value.date_started}
                        sx={{
                            textDecoration: 'none',
                            backgroundColor: theme[themeStyle].primary_orange,
                            color: theme[themeStyle].pure_black,
                            textTransform: 'none',
                            width: device.isMobile ? '100%' : '160px',
                            marginTop: '10px',
                            marginBottom: '10px',
                            '&:disabled': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                color: theme[themeStyle].darkGray60,
                            },
                            '&:hover': {
                                border: `2px solid ${theme[themeStyle].primary_orange}`,
                                backgroundColor: theme[themeStyle].pure_black,
                                color: theme[themeStyle].primary_orange,
                            },
                        }}>
                        {editingIndex !== null ? 'Update Phase' : 'Add Phase'}
                    </Button>
                </Box>
            </GenericModal>
            {!readOnly && (
                <Box sx={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <IconButton
                        color="primary"
                        aria-label="Add Avatar"
                        onClick={() => setOpen(true)}
                        style={{
                            borderRadius: '50%',
                            border: `1px solid ${theme[themeStyle].lightWhite}`,
                            marginLeft: 8,
                        }}>
                        <AddIcon style={{ height: 20, width: 20, color: theme[themeStyle].primary_orange }} />
                    </IconButton>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.REGULAR_HEADER,
                            fontSize: 8,
                            color: theme[themeStyle].tag_white,
                            marginTop: 1,
                        }}>
                        Add New Phase
                    </Typography>
                </Box>
            )}
        </Stack>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        text: {
            textAlign: 'left',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].form_label,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            height: 'fit-content',
        },
        detailsButton: {
            height: '52px',
            borderRadius: 100,
            fontSize: 16,
            fontWeight: '600',
            fontFamily: FONT_ENUM.REGULAR_HEADER,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 20,
            width: '100%',
        },
    });

export { ContestPhaseSelector };
